/* eslint-disable max-len */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Grid,
    Button,
    Typography,
    Paper
} from '@mui/material';
import {
    Email,
    Phone
} from '@mui/icons-material';

import { icons } from '@theme';
import { AutocompleteField } from '../CommonComponents/Form';
import { NotFound } from '../CommonComponents';
import AuthContext from './AuthContext';
import AuthContainer from './AuthContainer';

import { searchCarriers, searchBuyers } from '../../api-public/company';
import SimpleForm from '../CommonComponents/Form/SimpleForm';

const supportMailAddress = encodeURIComponent('"Logic Link Support" <support@logiclink.se>');
const supportMailSubject = encodeURIComponent('Vem är min lokala administratör?');
const supportMailUrl = `mailto:${supportMailAddress}?subject=${supportMailSubject}`;
const supportPhoneUrl = 'tel:0470-515110';

type CompanyType = 'carrier' | 'buyer';
export default function RegisterCompany() {
    const {
        values: {
            tenantId,
            tenantName,
            tenantIsRegistered
        },
        onChange
    } = React.useContext(AuthContext);

    const navigate = useNavigate();

    const { type } = useParams() as unknown as { type: CompanyType };
    const isCarrier = type === 'carrier';
    const isBuyer = type === 'buyer';
    const noParams = !type;

    if (noParams) {
        return (
            <AuthContainer>
                <Typography variant="h4" sx={{ textAlign: 'center', mb: 0, mt: 4 }}>
                    Välkommen!
                </Typography>
                <Typography variant="h6" sx={{ textAlign: 'center', mb: 4, mt: 4 }}>
                    Vi optimerar logistikkedjan genom att koppla samman rätt transportör med rätt uppdrag. Hur vill du använda plattformen?
                </Typography>
                <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                        <Paper
                            elevation={3}
                            onClick={() => navigate('/register-company/carrier')}
                            sx={{
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flex: 1,
                                width: '100%',
                                p: 4,
                                cursor: 'pointer',
                                transition: 'box-shadow 0.2s, transform 0.2s',
                                '&:hover': {
                                    boxShadow: 8,
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <icons.carrier
                                sx={{
                                    fontSize: 70,
                                    mb: 1,
                                    mt: 0.5,
                                    color: 'primary.main'
                                }}
                            />
                            <Typography variant="h6" sx={{ textAlign: 'center', minHeight: 36 }}>
                                Transportör
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    textAlign: 'center',
                                    mt: 1,
                                    minHeight: 72,
                                    maxWidth: 340,
                                    color: 'text.secondary'
                                }}
                            >
                                Hitta nya affärsmöjligheter, fyll din kapacitet och hantera egna uppdrag i vårt digitala ekosystem.
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                        <Paper
                            elevation={3}
                            onClick={() => navigate('/register-company/buyer')}
                            sx={{
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flex: 1,
                                width: '100%',
                                p: 4,
                                cursor: 'pointer',
                                transition: 'box-shadow 0.2s, transform 0.2s',
                                '&:hover': {
                                    boxShadow: 8,
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <icons.buyer
                                sx={{
                                    fontSize: 70,
                                    mb: 1,
                                    mt: 0.5,
                                    color: 'primary.main'
                                }}
                            />
                            <Typography variant="h6" sx={{ textAlign: 'center', minHeight: 36 }}>
                                Transportköpare
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    textAlign: 'center',
                                    mt: 1,
                                    minHeight: 72,
                                    maxWidth: 340,
                                    color: 'text.secondary'
                                }}
                            >
                                Nå ut till ett brett nätverk av kvalitetssäkrade transportörer. Publicera dina uppdrag och få konkurrenskraftiga prisförslag direkt.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Grid container spacing={2} justifyContent="space-between">
                    <Grid item xs>
                        <Button variant="text" onClick={() => navigate('/')}>
                            Tillbaka
                        </Button>
                    </Grid>
                </Grid>
            </AuthContainer>
        );
    }

    if (!isCarrier && !isBuyer) {
        return (
            <AuthContainer>
                <NotFound />
            </AuthContainer>
        );
    }

    return (
        <AuthContainer>
            <Typography variant="h5" sx={{ textAlign: 'center', mb: 6, mt: 4 }}>
                {`Anslut dig som ${isCarrier ? 'transportör' : 'transportköpare'}`}
            </Typography>
            <SimpleForm
                submitText="Nästa"
                onSubmit={() => navigate('/register-user')}
                submitDisabled={!tenantId || tenantIsRegistered}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <AutocompleteField<{
                            id: number | string,
                            name: string,
                            orgNumber?: string,
                            isRegistered?: boolean
                        }>
                            variant="outlined"
                            label="Välj ditt företag"
                            optionIdKey="id"
                            defaultOption={!tenantId ? null : {
                                id: tenantId,
                                name: tenantName || ''
                            }}
                            fetchOptions={isCarrier ? searchCarriers : searchBuyers}
                            minQueryLength={3}
                            getOptionName={(o) => o.name}
                            getOptionDescription={(o) => o.orgNumber || ''}
                            onChange={(company) => onChange({
                                tenantId: company === null ? null : company.id,
                                tenantName: company === null ? null : company.name,
                                tenantOrgNumber: company === null ? null : company.orgNumber,
                                tenantIsRegistered: company === null ? null : company.isRegistered
                            })}
                        />
                    </Grid>
                    {tenantIsRegistered ? (
                        <>
                            <Grid item xs={12} sx={{ m: 2, mb: 0 }}>
                                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                                    Ditt företag är redan registrerat.
                                    Kontakta oss om du inte vet vem din lokala administratör är.
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    startIcon={<Phone />}
                                    href={supportPhoneUrl}
                                    target="_blank"
                                >
                                    0470 - 51 51 10
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    startIcon={<Email />}
                                    href={supportMailUrl}
                                    target="_blank"
                                >
                                    support@logiclink.se
                                </Button>
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12} sx={{ textAlign: 'center', m: 2, mb: 0 }}>
                            <Typography variant="body1" sx={{ textAlign: 'center' }}>
                                {/* eslint-disable-next-line max-len */}
                                {isCarrier && 'Vi uppdaterar kontinuerligt vårt register med transportörer som uppfyller våra kriterier. '}
                                Hittar du inte ditt företag i listan men vill ansluta dig ändå?
                            </Typography>
                            <Button
                                variant="text"
                                onClick={() => navigate(isCarrier ? '/create-company/carrier' : '/create-company/buyer')}
                            >
                                Klicka här
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </SimpleForm>

            <Grid container spacing={2} justifyContent="space-between">
                <Grid item xs>
                    <Button variant="text" onClick={() => navigate('/register-company')}>
                        Tillbaka
                    </Button>
                </Grid>
            </Grid>
        </AuthContainer>
    );
}
